import { HelpLink, Related, ScreenLink } from "./parts";

export function WhatNexusDoes() {
  return (
    <>
      <p>
        Nexus by AllCode lets a customer's developers use Claude and other Amazon Bedrock models through a proxy that runs in the
        customer's own AWS account. The proxy checks who is calling, sends the request to Bedrock, and records every
        request's tokens. Nexus then prices that usage and bills it. This admin app is where AllCode staff set up
        customers, price and bill their usage, and install and upgrade the proxy.
      </p>

      <h2 id="the-parts">The parts</h2>
      <ul>
        <li>
          <strong>The proxy</strong> runs in the customer's AWS account. Developers point Claude Code, Codex or an SDK at
          its URL. It speaks the Anthropic Messages, OpenAI Responses and OpenAI Chat Completions APIs, and forwards to
          Bedrock in that account. See <HelpLink to="claude-code-and-codex" />.
        </li>
        <li>
          <strong>The Usage API</strong> runs in AllCode's account. Each proxy sends it a usage event per request (who,
          which model, how many tokens of each type). It keeps the ledger and serves the reports.
        </li>
        <li>
          <strong>The worker</strong> rolls usage up by day, reads the customers' AWS Cost and Usage Reports, and opens a
          billing month for every customer.
        </li>
        <li>
          <strong>This admin app</strong> is a thin client of the Usage API. Every change you make here is recorded in the{" "}
          <HelpLink to="audit-log" />.
        </li>
      </ul>
      <p>
        Prompt text is never shown in this app. Where a customer switches prompt logging on, only people the customer
        authorises can read it, through the API.
      </p>

      <h2 id="the-screens">The screens</h2>
      <p>The sidebar groups the screens by job. You see only the ones your role may open.</p>
      <table>
        <thead>
          <tr>
            <th>Screen</th>
            <th>What it is for</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <ScreenLink href="/">Overview</ScreenLink>
            </td>
            <td>This month at a glance, and what needs attention.</td>
          </tr>
          <tr>
            <td>
              <ScreenLink href="/customers">Customers</ScreenLink>
            </td>
            <td>
              Each customer, with tabs for Usage, People, AWS accounts, Sign-in, Keys, Models, Guardrails, Quotas,
              Notifications, Activity, Billing, Settings and Assistant.
            </td>
          </tr>
          <tr>
            <td>
              <ScreenLink href="/invoices">Invoices</ScreenLink>
            </td>
            <td>Every customer's months: open, due for closing, closed.</td>
          </tr>
          <tr>
            <td>
              <ScreenLink href="/rate-plans">Rate plans</ScreenLink>
            </td>
            <td>The markup, rates, tiers, fees and minimums a customer is billed on.</td>
          </tr>
          <tr>
            <td>
              <ScreenLink href="/model-prices">Model prices</ScreenLink>
            </td>
            <td>AWS list prices per Bedrock model, by date.</td>
          </tr>
          <tr>
            <td>
              <ScreenLink href="/revenue">Revenue &amp; margin</ScreenLink>
            </td>
            <td>Billed, AWS cost and gross profit by customer, model and month, and the forecast.</td>
          </tr>
          <tr>
            <td>
              <ScreenLink href="/aws-costs">AWS costs</ScreenLink>
            </td>
            <td>Which AWS cost profit is measured against, and the customers' Cost and Usage Reports.</td>
          </tr>
          <tr>
            <td>
              <ScreenLink href="/reconciliation">Reconciliation</ScreenLink>
            </td>
            <td>Checks that metered usage matches what Bedrock recorded.</td>
          </tr>
          <tr>
            <td>
              <ScreenLink href="/installer">Installer</ScreenLink>
            </td>
            <td>Link a customer's AWS account and install or upgrade the proxy there.</td>
          </tr>
          <tr>
            <td>
              <ScreenLink href="/audit">Audit log</ScreenLink>
            </td>
            <td>Who changed what, and when.</td>
          </tr>
          <tr>
            <td>
              <ScreenLink href="/help">Help</ScreenLink>
            </td>
            <td>These articles.</td>
          </tr>
        </tbody>
      </table>
      <p>
        Every form label has a <strong>?</strong> button. It opens a short explanation with an example and a link to the
        matching part of these articles.
      </p>

      <h2 id="overview-screen">The Overview screen</h2>
      <p>
        <ScreenLink href="/">Overview</ScreenLink> is the first page for super admins. It shows the current month so far,
        in UTC. What it shows depends on your role: a part whose data your role cannot read is left out.
      </p>
      <ul>
        <li>
          <strong>Headline figures</strong>: billed this month, gross profit with its margin %, active customers, and
          tokens used. Figures for the open month are month to date and can still change until the month is closed.
        </li>
        <li>
          <strong>Billed, last 12 months</strong>: a bar chart with the same numbers in a table. Open{" "}
          <ScreenLink href="/revenue">Revenue &amp; margin</ScreenLink> for the full breakdown.
        </li>
        <li>
          <strong>Needs your attention</strong>: work waiting on someone, such as months that have ended but are not
          closed, or installer changes waiting for approval. Each item links to where you deal with it.
        </li>
        <li>
          <strong>Top customers this month</strong>, by amount billed.
        </li>
      </ul>
      <p>
        Billing staff land on <ScreenLink href="/invoices">Invoices</ScreenLink> and installer operators on{" "}
        <ScreenLink href="/installer">Installer</ScreenLink> instead. Overview stays one click away in the sidebar.
      </p>

      <Related to={["signing-in-and-roles", "glossary", "pricing-and-markup", "revenue-and-margin"]} />
    </>
  );
}
